import { FC } from 'react';

import {
  Alert,
  Badge,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Table,
  Tag,
  Timeline,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  IPaymentIncident,
  IPaymentWebhookEvent,
  IPaymentWebhookRequest,
} from 'common/types/payment-diagnostics';
import { cents2dollars, parseDate } from 'common/utils';
import { Spinner } from 'components/Spinner';
import { observer } from 'mobx-react-lite';

import {
  attemptStatusColor,
  eventOutcomeColor,
  formatDuration,
  hasDetails,
  severityAlertType,
  severityTagColor,
  severityTimelineColor,
  webhookOutcomeColor,
} from '../helpers';
import { PaymentLogsViewModel } from '../payment-logs-view-model';

import styles from './diagnostics-drawer.module.scss';

interface IDiagnosticsDrawerProps {
  model: PaymentLogsViewModel;
}

const buildIncidentColumns = (
  onResolve: (id: number) => void,
): ColumnsType<IPaymentIncident> => [
  { title: 'Type', dataIndex: 'type', key: 'type' },
  {
    title: 'Severity',
    dataIndex: 'severity',
    key: 'severity',
    width: '10%',
    render: (severity) => <Tag color={severityTagColor(severity)}>{severity}</Tag>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: '10%',
    render: (status) => (
      <Tag color={status === 'OPEN' ? 'red' : 'green'}>{status}</Tag>
    ),
  },
  {
    title: 'Occurrences',
    dataIndex: 'occurrenceCount',
    key: 'occurrenceCount',
    width: '10%',
  },
  {
    title: 'First detected',
    dataIndex: 'firstDetectedAt',
    key: 'firstDetectedAt',
    render: (date) => parseDate(date),
  },
  {
    title: 'Last checked',
    dataIndex: 'lastCheckedAt',
    key: 'lastCheckedAt',
    render: (date) => (date ? parseDate(date) : '—'),
  },
  {
    title: 'Last error',
    dataIndex: 'lastError',
    key: 'lastError',
    render: (error) => error ?? '—',
  },
  {
    title: '',
    key: 'actions',
    width: 90,
    render: (_, incident) =>
      incident.status === 'OPEN' ? (
        <Button danger size="small" onClick={() => onResolve(incident.id)}>
          Close
        </Button>
      ) : null,
  },
];

const webhookColumns: ColumnsType<IPaymentWebhookEvent> = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: '6%' },
  { title: 'Provider', dataIndex: 'provider', key: 'provider' },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status) => (
      <Tag color={status === 'COMPLETED' ? 'green' : 'orange'}>{status}</Tag>
    ),
  },
  {
    title: 'Attempts',
    dataIndex: 'attemptCount',
    key: 'attemptCount',
    width: '8%',
  },
  {
    title: 'Received',
    dataIndex: 'receivedAt',
    key: 'receivedAt',
    render: (date) => parseDate(date),
  },
  {
    title: 'Processed',
    dataIndex: 'processedAt',
    key: 'processedAt',
    render: (date) => (date ? parseDate(date) : '—'),
  },
  {
    title: 'Last error',
    dataIndex: 'lastError',
    key: 'lastError',
    render: (error) => error ?? '—',
  },
];

const webhookRequestColumns: ColumnsType<IPaymentWebhookRequest> = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: '6%' },
  {
    title: 'Outcome',
    dataIndex: 'outcome',
    key: 'outcome',
    render: (outcome) => (
      <Tag color={webhookOutcomeColor(outcome)}>{outcome}</Tag>
    ),
  },
  {
    title: 'HTTP',
    dataIndex: 'httpStatus',
    key: 'httpStatus',
    width: '8%',
    render: (status) => status ?? '—',
  },
  {
    title: 'Duration',
    dataIndex: 'durationMs',
    key: 'durationMs',
    width: '10%',
    render: (duration) => formatDuration(duration),
  },
  {
    title: 'Received',
    dataIndex: 'receivedAt',
    key: 'receivedAt',
    render: (date) => parseDate(date),
  },
  {
    title: 'Completed',
    dataIndex: 'completedAt',
    key: 'completedAt',
    render: (date) => (date ? parseDate(date) : '—'),
  },
  {
    title: 'Error',
    dataIndex: 'errorMessage',
    key: 'errorMessage',
    render: (message, record) =>
      message ? `${record.errorCode ?? ''} ${message}`.trim() : '—',
  },
];

const Section: FC<{
  title: string;
  count: number;
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, count, extra, children }) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>
      {title} <span className={styles.sectionCount}>({count})</span>
      {!!extra && <span className={styles.sectionExtra}>{extra}</span>}
    </div>
    {children}
  </div>
);

export const DiagnosticsDrawer: FC<IDiagnosticsDrawerProps> = observer(
  ({ model }) => {
    const { detail, detailLoading, detailTitle, isDetailOpen } = model;
    const openIncidentIds = model.openDetailIncidentIds;
    const incidentColumns = buildIncidentColumns((id) =>
      model.openResolveModal([id]),
    );

    return (
      <Drawer
        open={isDetailOpen}
        onClose={model.closeDetail}
        title={`Payment diagnostics — ${detailTitle}`}
        width="70%"
        destroyOnClose
      >
        {detailLoading && (
          <div className={styles.loading}>
            <Spinner type="large" />
          </div>
        )}

        {!detailLoading && !detail && (
          <Empty description="No diagnostics data" />
        )}

        {!detailLoading && !!detail && (
          <div className={styles.content}>
            <Alert
              type={severityAlertType(detail.conclusion.severity)}
              message={detail.conclusion.code}
              description={detail.conclusion.message}
              showIcon
            />

            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Order">
                {detail.summary.orderId ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Attempt status">
                {detail.summary.attemptStatus ? (
                  <Tag color={attemptStatusColor(detail.summary.attemptStatus)}>
                    {detail.summary.attemptStatus}
                  </Tag>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Provider">
                {detail.summary.provider ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Method">
                {detail.summary.method ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Expected amount">
                {detail.summary.expectedAmount !== null
                  ? `${cents2dollars(detail.summary.expectedAmount)} ${
                      detail.summary.expectedCurrency ?? ''
                    }`.trim()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Intent ID">
                <span className={styles.mono}>
                  {detail.summary.providerIntentId ?? '—'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Webhook received">
                <Badge
                  status={detail.summary.webhookReceived ? 'success' : 'error'}
                  text={detail.summary.webhookReceived ? 'Yes' : 'No'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Transaction created">
                <Badge
                  status={
                    detail.summary.transactionCreated ? 'success' : 'error'
                  }
                  text={detail.summary.transactionCreated ? 'Yes' : 'No'}
                />
              </Descriptions.Item>
            </Descriptions>

            <Section title="Timeline" count={detail.timeline.length}>
              {detail.timeline.length ? (
                <Timeline
                  className={styles.timeline}
                  items={detail.timeline.map((event) => ({
                    color: severityTimelineColor(event.severity),
                    children: (
                      <div className={styles.event}>
                        <div className={styles.eventHeader}>
                          <span className={styles.eventType}>
                            {event.eventType}
                          </span>
                          <Tag color={eventOutcomeColor(event.outcome)}>
                            {event.outcome}
                          </Tag>
                          <Tag>{event.source}</Tag>
                          <span className={styles.eventTime}>
                            {parseDate(event.occurredAt)}
                          </span>
                        </div>
                        {!!event.message && (
                          <div className={styles.eventMessage}>
                            {event.message}
                          </div>
                        )}
                        {!!event.code && (
                          <div className={styles.eventCode}>{event.code}</div>
                        )}
                        {hasDetails(event.details) && (
                          <pre className={styles.eventDetails}>
                            {JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No events"
                />
              )}
            </Section>

            <Section
              title="Incidents"
              count={detail.incidents.length}
              extra={
                openIncidentIds.length > 1 && (
                  <Button
                    danger
                    size="small"
                    type="primary"
                    onClick={() => model.openResolveModal(openIncidentIds)}
                  >
                    Close all open ({openIncidentIds.length})
                  </Button>
                )
              }
            >
              <Table
                rowKey="id"
                size="small"
                bordered
                columns={incidentColumns}
                dataSource={detail.incidents}
                pagination={false}
                scroll={{ x: true }}
              />
            </Section>

            <Section title="Webhook events" count={detail.webhooks.length}>
              <Table
                rowKey="id"
                size="small"
                bordered
                columns={webhookColumns}
                dataSource={detail.webhooks}
                pagination={false}
                scroll={{ x: true }}
              />
            </Section>

            <Section
              title="Webhook requests"
              count={detail.webhookRequests.length}
            >
              <Table
                rowKey="id"
                size="small"
                bordered
                columns={webhookRequestColumns}
                dataSource={detail.webhookRequests}
                pagination={false}
                scroll={{ x: true }}
              />
            </Section>
          </div>
        )}
      </Drawer>
    );
  },
);
